import { MainLayout } from '@/components/layout/MainLayout';
import { useMultitrack } from '@/components/multitrack/useMultitrack';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Volume2, VolumeX, Play, Pause } from 'lucide-react';
import { useStemSession } from '@/hooks/useStemSession';
import { useEffect } from 'react';
const MultitrackTest = () => {
  const {
    sessions,
    activeSession,
    setActiveSession
  } = useStemSession();
  useEffect(() => {
    if (sessions?.length && !activeSession.id) {
      const completedSessions = sessions.filter(s => s.status === 'completed');
      if (completedSessions.length > 0) {
        const mostRecent = completedSessions.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
        setActiveSession({
          id: mostRecent.id,
          type: 'stem'
        });
      }
    }
  }, [sessions, activeSession.id, setActiveSession]);
  const currentSession = sessions?.find(s => s.id === activeSession.id);
  const stems = currentSession?.stems as {
    name: string;
    url: string;
    duration: number;
  }[] || [];
  console.log('MultitrackTest - Current Session:', currentSession);
  console.log('MultitrackTest - Available Stems:', stems);
  const {
    containerRef,
    isPlaying,
    tracks,
    masterVolume,
    togglePlayPause,
    setTrackVolume,
    setTrackMute,
    setTrackSolo,
    setGlobalVolume
  } = useMultitrack(stems, currentSession?.original_audio_url || '');
  if (!sessions?.length) {
    return <MainLayout>
        <div className="p-8">
          <h1 className="text-2xl font-bold text-white mb-4">Multitrack Player Test</h1>
          <p className="text-muted-foreground">
            Please process a track in the AI Stem Splitter first to test the multitrack player.
          </p>
        </div>
      </MainLayout>;
  }
  if (!currentSession) {
    return <MainLayout>
        <div className="p-8">
          <h1 className="text-2xl font-bold text-white mb-4">Multitrack Player Test</h1>
          <div className="space-y-4">
            <p className="text-muted-foreground">Select a processed track to test:</p>
            <div className="space-y-2">
              {sessions.filter(s => s.status === 'completed').map(session => <Button key={session.id} variant="outline" onClick={() => setActiveSession({
              id: session.id,
              type: 'stem'
            })}>
                  {session.original_filename}
                </Button>)}
            </div>
          </div>
        </div>
      </MainLayout>;
  }
  return <MainLayout>
      <div className="p-8 space-y-8">
        <h1 className="text-2xl font-bold text-white">Multitrack Player Test</h1>
        
        <div className="flex items-center gap-6">
          <Button variant="outline" size="icon" onClick={togglePlayPause} className="h-12 w-12">
            {isPlaying ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6" />}
          </Button>

          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-muted-foreground">Master</span>
            <div className="flex items-center gap-2 w-48">
              <VolumeX className="h-4 w-4" />
              <Slider value={[masterVolume * 100]} onValueChange={value => setGlobalVolume(value[0] / 100)} max={100} step={1} />
              <Volume2 className="h-4 w-4" />
            </div>
          </div>
        </div>

        <div className="flex">
          <div className="w-[360px] flex flex-col gap-[2px] mr-4">
            {tracks.map(track => <div key={track.id} className="flex items-center gap-4 px-4 bg-card h-[48px]">
                <span className="w-24 text-sm font-medium truncate">{track.name}</span>
                
                <div className="flex items-center gap-2 w-48">
                  <VolumeX className="h-4 w-4" />
                  
                  <Volume2 className="h-4 w-4" />
                </div>

                <div className="flex items-center gap-2">
                  <Button variant={track.isMuted ? "destructive" : "outline"} size="sm" onClick={() => setTrackMute(track.id)} className="h-8">
                    {track.isMuted ? 'Unmute' : 'Mute'}
                  </Button>

                  <Button variant={track.isSoloed ? "default" : "outline"} size="sm" onClick={() => setTrackSolo(track.id)} className="h-8">
                    {track.isSoloed ? 'Unsolo' : 'Solo'}
                  </Button>
                </div>
              </div>)}
          </div>

          <div ref={containerRef} className="flex-1 bg-black/20 rounded-lg overflow-hidden" style={{
          minHeight: '300px'
        }} />
        </div>

        <div className="text-sm text-muted-foreground">
          <p>Currently playing: {currentSession.original_filename}</p>
          <p className="mt-2">Testing Notes:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Use the AI Stem Splitter to process tracks</li>
            <li>Test all controls: play/pause, volume, mute, solo</li>
            <li>Check waveform rendering and synchronization</li>
            <li>Verify track isolation when using solo/mute</li>
          </ul>
        </div>
      </div>
    </MainLayout>;
};
export default MultitrackTest;